import React from "react";
import { useTranslation } from "react-i18next";
import { Form, Input, InputNumber, Select, Switch, DatePicker } from "antd";
import moment from "moment";
import styles from "./ViewReward.module.scss";

export interface ViewRewardGeneralTabProps {
  reward: any;
  canEdit: boolean;
  rewardTypes: any[];
  onSearchRewardTypes: (value: string) => void;
  getOptions: (items: any) => React.ReactNode;
}

const ViewRewardGeneralTab: React.FC<ViewRewardGeneralTabProps> = ({
  reward,
  canEdit,
  rewardTypes,
  onSearchRewardTypes,
  getOptions,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <div className={styles.sectionTitle}>{t("Common-BasicInformation")}</div>
      <div className={styles.itemWrapper}>
        <Form.Item
          className={styles.formItem}
          initialValue={reward?.name}
          label={t("Common-Name")}
          name="name"
          rules={[{ required: true, message: t("Common-Name") }]}
        >
          <Input disabled={!canEdit} placeholder={t("Common-Name")} />
        </Form.Item>
        <Form.Item
          className={styles.formItem}
          label={t("Common-Type")}
          name="rewardType"
          initialValue={reward?.type?.id}
          rules={[{ required: true, message: t("Common-Type") }]}
        >
          <Select
            allowClear
            showSearch
            filterOption={(input: string, option: any) =>
              option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            onSearch={onSearchRewardTypes}
            placeholder={t("Common-SearchByName")}
          >
            {getOptions(rewardTypes)}
          </Select>
        </Form.Item>
      </div>
      <div className={styles.itemWrapper}>
        <Form.Item
          className={styles.formItem}
          label={t("Common-Description")}
          name="description"
          initialValue={reward?.description}
        >
          <Input placeholder={t("Common-Description")} disabled={!canEdit} />
        </Form.Item>
        <Form.Item
          className={styles.formItem}
          label={t("Common-Active")}
          initialValue={reward?.status}
          name="status"
        >
          <Switch defaultChecked={reward?.status} disabled={!canEdit} />
        </Form.Item>
      </div>

      <div className={styles.sectionTitle}>{t("Common-Configuration")}</div>
      <div className={styles.itemWrapper}>
        <Form.Item
          className={styles.formItem}
          label={t("Common-Value")}
          name="value"
          initialValue={reward?.value}
        >
          <InputNumber
            className={styles.inputNumber}
            min={0}
            placeholder={t("Common-Value")}
            disabled={!canEdit}
          />
        </Form.Item>
        <Form.Item
          className={styles.formItem}
          label={t("Common-PointsValue")}
          name="pointsValue"
          initialValue={reward?.pointsValue}
        >
          <InputNumber
            className={styles.inputNumber}
            min={0}
            placeholder={t("Common-PointsValue")}
            disabled={!canEdit}
          />
        </Form.Item>
      </div>
      <div className={styles.itemWrapper}>
        <Form.Item
          className={styles.formItem}
          label={t("Common-AccumulatedCost")}
          name="accumulatedCost"
          initialValue={reward?.accumulatedCost}
        >
          <InputNumber
            className={styles.inputNumber}
            min={0}
            step={0.01}
            placeholder={t("Common-AccumulatedCost")}
            disabled
          />
        </Form.Item>
      </div>
      <div className={styles.itemWrapper}>
        <Form.Item
          className={styles.formItem}
          label={t("Common-EstimatedImpact")}
          name="estimatedImpact"
          initialValue={reward?.estimatedImpact}
        >
          <InputNumber
            className={styles.inputNumber}
            min={0}
            step={0.01}
            placeholder={t("Common-EstimatedImpact")}
            disabled
          />
        </Form.Item>
        <Form.Item
          className={styles.formItem}
          label={t("Common-AccumulatedImpact")}
          name="accumulatedImpact"
          initialValue={reward?.accumulatedImpact}
        >
          <InputNumber
            className={styles.inputNumber}
            min={0}
            step={0.01}
            placeholder={t("Common-AccumulatedImpact")}
            disabled
          />
        </Form.Item>
      </div>
      <div className={styles.itemWrapper}>
        <Form.Item
          className={styles.formItem}
          label={t("Common-EstimatedReach")}
          name="estimatedReach"
          initialValue={reward?.estimatedReach}
        >
          <InputNumber
            className={styles.inputNumber}
            min={0}
            step={1}
            placeholder={t("Common-EstimatedReach")}
            disabled
          />
        </Form.Item>
        <div className={styles.formItem} />
      </div>
      <div className={styles.itemWrapper}>
        <Form.Item
          className={styles.formItem}
          label={t("Common-Valid")}
          name="validity"
          initialValue={[
            reward?.validityStart ? moment(reward.validityStart, "YYYY-MM-DD") : null,
            reward?.validityEnd ? moment(reward.validityEnd, "YYYY-MM-DD") : null,
          ]}
        >
          <DatePicker.RangePicker
            className={styles.dateSelector}
            placeholder={[t("Common-ValidFrom"), t("Common-ValidTo")]}
            disabled={!canEdit}
          />
        </Form.Item>
        <div className={styles.formItem} />
      </div>
    </>
  );
};

export default ViewRewardGeneralTab;
